
import { useLocation, useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { EChecklistTaskStatus } from 'common/contracts';
import { EEventsType } from 'constants/enums';
import { useBigQueryEventsTracking } from 'hooks/useBigQueryTracking';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';
import { trackMixpanelEvent } from 'utils/mixpanelClient';
import { getSolutionModelInfo } from 'utils/projectTypeMapping';

import { cn } from 'components/lib/utils';

import { ISetupChecklistTask } from '../types';

interface ISetupChecklistTaskProps {
  task: ISetupChecklistTask;
}



const mapStatusToIcon: Partial<Record<EChecklistTaskStatus, React.ReactNode>> =
  {
    done: (
      <FontAwesomeIcon
        icon={`fa-solid fa-circle-check`}
        className="text-xs text-indigo-500"
      />
    ),
    open: <FontAwesomeIcon icon={`fa-regular fa-circle`} className="text-xs" />,
    pending: (
      <FontAwesomeIcon
        icon={`fa-regular fa-circle-dashed`}
        className="text-xs"
      />
    )
  };

export const SetupChecklistTask: React.FC<ISetupChecklistTaskProps> = ({
  task
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const publisherId = location.pathname.split('/project/')[1]?.split('/')[0];
  const { setTaskStatus, toggleChecklistExpandedState, isExpanded, isWebStore, isPaymentLinks } = useChecklistState();
  const { trackBigQueryEvent } = useBigQueryEventsTracking();

  const { status, title, action, locked } = task;


  const isLocked = status === EChecklistTaskStatus.LOCKED || locked;

  const markDone = () => {
    setTaskStatus(task.id);
  };

  return (
    <div
      className={cn(
        'w-full text-base-mutedForeground text-xs mt-3',
        action && !isLocked && 'cursor-pointer',
        isLocked && 'opacity-60'
      )}
      onClick={() => {
        if (isLocked) return; // Prevent action if locked
        
        action?.({
          navigate,
          markTaskAsDone: markDone,
          publisherId,
          toggleChecklist: toggleChecklistExpandedState
        });
      
        if (action && isExpanded) {
          toggleChecklistExpandedState(false);
        }
      
        if (isExpanded && action) {
          const solutionModelInfo = getSolutionModelInfo(isWebStore, isPaymentLinks);
          
          trackMixpanelEvent(EEventsType.DASHBOARD_SETUP_GUIDE_TASK_CLICKED, {
            task_name: title,
            task_status: status,
            solution_model: solutionModelInfo,       
          });
          trackBigQueryEvent({
            eventName: EEventsType.DASHBOARD_SETUP_GUIDE_TASK_CLICKED,
            taskName: title,
            taskStatus: status
          });
        }
      }}
    >
      <div className="flex items-start gap-2 min-w-0">
        <div className="flex-shrink-0">
          {!isLocked && mapStatusToIcon[status as EChecklistTaskStatus]}
        </div>
        <div className="flex-1 min-w-0 text-start break-words">{title}</div>
        {isLocked && (
          <FontAwesomeIcon
            icon={`fa-solid fa-lock text-base-mutedForeground`}
            className="text-xs self-end"
          />
        )}
      </div>
    </div>
  );
};
